import React, { useState, useEffect } from 'react';
import { SignedIn, SignedOut, ClerkLoaded, ClerkLoading, useClerk } from '@clerk/clerk-react';
import './App.css';
import Sidebar, { sectionLabel } from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Estadisticas from './components/Estadisticas';
import Equipo from './components/Equipo';
import GanttChart from './components/GanttChart';
import GanttProjects from './components/GanttProjects';
import CronogramaProyectos from './components/CronogramaProyectos';
import Leads from './components/Leads';
import WhatsAppMessages from './components/WhatsAppMessages';
import Journal from './components/Journal';
import Etiquetas from './components/Etiquetas';
import Manuales from './components/Manuales';
import MetradosCostos from './components/MetradosCostos';
import Configuracion from './components/Configuracion';
import LoginScreen from './components/LoginScreen';
import { useWhitelistRole } from './useWhitelistRole';
import useBreakpoint from './hooks/useBreakpoint';

function AuthLoader({ label = 'másquemuros' }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
      background: 'linear-gradient(180deg, #6B1C2A 0%, #4A1220 100%)',
      color: '#C9A96E',
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 28,
      fontStyle: 'italic',
      letterSpacing: '0.05em',
    }}>
      <div>{label}</div>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: '2.5px solid rgba(201,169,110,0.25)',
        borderTopColor: '#C9A96E',
        animation: 'mqm-spin 0.9s linear infinite',
      }} />
      <style>{`@keyframes mqm-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AccessDenied({ email, error }) {
  const { signOut } = useClerk();
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      background: 'linear-gradient(180deg, #6B1C2A 0%, #4A1220 100%)',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 18,
        padding: '40px 44px',
        maxWidth: 460,
        width: '100%',
        boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
        textAlign: 'center',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(184,59,59,0.1)', color: '#B83B3B',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        </div>
        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 30, fontWeight: 600, color: '#6B1C2A',
          margin: '0 0 10px',
        }}>
          Acceso denegado
        </h1>
        <p style={{ color: '#6B6B6B', fontSize: 14, lineHeight: 1.55, margin: '0 0 8px' }}>
          No tienes acceso a esta plataforma. Contacta al administrador.
        </p>
        {email && (
          <p style={{ color: '#9a8a8a', fontSize: 12, margin: '0 0 20px', fontFamily: 'monospace' }}>
            {email}
          </p>
        )}
        {error && (
          <p style={{ color: '#B83B3B', fontSize: 11, marginTop: 0, marginBottom: 20 }}>
            Error: {error}
          </p>
        )}
        <button
          type="button"
          onClick={() => signOut()}
          style={{
            padding: '10px 22px',
            borderRadius: 8,
            border: 'none',
            background: '#6B1C2A',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <>
      <ClerkLoading>
        <AuthLoader />
      </ClerkLoading>
      <ClerkLoaded>
        <SignedOut>
          <LoginScreen />
        </SignedOut>
        <SignedIn>
          <WhitelistGate />
        </SignedIn>
      </ClerkLoaded>
    </>
  );
}

function WhitelistGate() {
  const { authorized, loading, email, error } = useWhitelistRole();
  if (loading) return <AuthLoader label="Verificando acceso…" />;
  if (!authorized) return <AccessDenied email={email} error={error} />;
  return <AuthenticatedApp />;
}

// localStorage helpers — guarded for environments that block storage
// (private mode, SSR placeholders, etc.). All three keys live under the
// mqm_ prefix so they're easy to wipe together if needed.
function readLS(key, fallback = null) {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function writeLS(key, value) {
  try {
    if (value == null || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* ignore */ }
}

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function AuthenticatedApp() {
  const { isAdmin } = useWhitelistRole();
  const { isCompact } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Restore persisted session state. This component only mounts after
  // the user is both Clerk-authenticated and whitelist-authorized (see
  // WhitelistGate above), so persistence restore always happens on a
  // verified session — never on a signed-out / denied user.
  const [view, setView] = useState(() => readLS('mqm_current_view', 'dashboard'));
  const [ganttProjectId, setGanttProjectId] = useState(() => readLS('mqm_gantt_project', null));
  const [ganttType, setGanttType] = useState(() => readLS('mqm_gantt_type', null)); // 'diseño' | 'obra' | null
  const [ganttFocusTaskId, setGanttFocusTaskId] = useState(null);
  // Set when Leads sends a lead to the Dashboard's "Nuevo Proyecto" modal.
  const [dashboardPrefill, setDashboardPrefill] = useState(null);

  // Persist whenever these change.
  useEffect(() => { writeLS('mqm_current_view', view); }, [view]);
  useEffect(() => { writeLS('mqm_gantt_project', ganttProjectId); }, [ganttProjectId]);
  useEffect(() => { writeLS('mqm_gantt_type', ganttType); }, [ganttType]);

  // Drawer closes once the viewport is back to desktop.
  useEffect(() => { if (!isCompact) setDrawerOpen(false); }, [isCompact]);

  function navigate(nextView, opts = {}) {
    if (nextView === 'dashboard' && opts.leadPrefill) {
      setDashboardPrefill(opts.leadPrefill);
      setView('dashboard');
      return;
    }
    if (nextView === 'gantt') {
      if (opts.projectId) {
        setGanttProjectId(opts.projectId);
        setGanttType(opts.ganttType || null);
        setGanttFocusTaskId(opts.taskId || null);
        setView('gantt-chart');
        return;
      }
      setGanttProjectId(null);
      setGanttType(null);
      setGanttFocusTaskId(null);
      setView('gantt');
      return;
    }
    setView(nextView);
  }

  function openGantt(projectId, type) {
    setGanttProjectId(projectId);
    setGanttType(type);
    setGanttFocusTaskId(null);
    setView('gantt-chart');
  }

  function backToGanttProjects() {
    setGanttProjectId(null);
    setGanttType(null);
    setGanttFocusTaskId(null);
    setView('gantt');
  }

  const sidebarActive = view === 'gantt-chart' ? 'gantt' : view;

  // Same section content for both shells.
  const content = (
    <>
        {view === 'dashboard' && (
          <Dashboard
            onNavigate={navigate}
            prefill={dashboardPrefill}
            onPrefillConsumed={() => setDashboardPrefill(null)}
          />
        )}
        {view === 'leads' && isAdmin && <Leads onNavigate={navigate} />}
        {view === 'leads' && !isAdmin && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>
            No tienes permisos para ver esta sección.
          </div>
        )}
        {view === 'estadisticas' && isAdmin && <Estadisticas onNavigate={navigate} />}
        {view === 'estadisticas' && !isAdmin && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>
            No tienes permisos para ver esta sección.
          </div>
        )}
        {view === 'manuales' && <Manuales />}
        {view === 'journal' && <Journal />}
        {view === 'equipo' && <Equipo onNavigate={navigate} />}
        {view === 'etiquetas' && <Etiquetas />}
        {view === 'metrados' && <MetradosCostos />}
        {view === 'gantt' && <GanttProjects onOpen={openGantt} />}
        {view === 'cronograma' && <CronogramaProyectos />}
        {view === 'gantt-chart' && (
          <GanttChart
            initialProjectId={ganttProjectId}
            ganttType={ganttType}
            initialTaskId={ganttFocusTaskId}
            onBack={backToGanttProjects}
          />
        )}
        {view === 'whatsapp' && isAdmin && <WhatsAppMessages />}
        {view === 'whatsapp' && !isAdmin && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>
            No tienes permisos para ver esta sección.
          </div>
        )}
        {view === 'configuracion' && isAdmin && <Configuracion />}
        {view === 'configuracion' && !isAdmin && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>
            No tienes permisos para ver esta sección.
          </div>
        )}
    </>
  );

  if (isCompact) {
    return (
      <div style={styles.appCompact}>
        <header style={styles.topBar}>
          <button
            type="button"
            style={styles.hamburger}
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menú"
            aria-expanded={drawerOpen}
          >
            <HamburgerIcon />
          </button>
          <span style={styles.topBarTitle}>{sectionLabel(sidebarActive)}</span>
        </header>
        <Sidebar
          active={sidebarActive}
          onNavigate={(v, o) => { setDrawerOpen(false); navigate(v, o); }}
          variant="drawer"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
        <main style={styles.mainCompact}>{content}</main>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      <Sidebar active={sidebarActive} onNavigate={navigate} />
      <main style={styles.main}>
        {content}
      </main>
    </div>
  );
}

const styles = {
  app: {
    display: 'flex',
    minHeight: '100vh',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    background: 'var(--beige)',
  },
  // ── Compact (< 1024px) shell only ──
  appCompact: {
    minHeight: '100vh',
    background: 'var(--beige)',
  },
  topBar: {
    position: 'sticky',
    top: 0,
    zIndex: 900,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 56,
    padding: 'env(safe-area-inset-top) max(8px, env(safe-area-inset-right)) 0 max(4px, env(safe-area-inset-left))',
    background: 'linear-gradient(180deg, #6B1C2A 0%, #4A1220 100%)',
    color: '#fff',
    boxShadow: '0 2px 10px rgba(74,18,32,0.25)',
  },
  hamburger: {
    width: 44,
    height: 44,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 10,
    background: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    flexShrink: 0,
  },
  topBarTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 21,
    fontWeight: 600,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  mainCompact: {
    minWidth: 0,
    width: '100%',
    overflowX: 'hidden',
    background: 'var(--beige)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
    boxSizing: 'border-box',
  },
};

export default App;
